import { StateCreator } from 'zustand';
import { Message } from '../../types';
import { supabase } from '../../config/supabase';
import { logger } from '../../utils/logger';

export interface MessageSlice {
  messages: Message[];
  messageHistory: Message[][];
  searchQuery: string;
  addMessage: (message: Message) => void;
  acknowledgeMessage: (messageId: string, userId: string) => Promise<void>;
  archiveMessage: (messageId: string) => Promise<void>;
  editMessage: (messageId: string, updates: Partial<Message> | Message) => void;
  undoLastAction: () => void;
  setSearchQuery: (query: string) => void;
  getFilteredMessages: () => Message[];
}

export const createMessageSlice: StateCreator<MessageSlice> = (set, get) => ({
  messages: [],
  messageHistory: [],
  searchQuery: '',
  
  addMessage: (message) => {
    set((state) => {
      const allDepartments = [...new Set([
        ...message.departments,
        ...(message.recipients?.map(r => r.department) || []),
        message.sender.department
      ])];

      const newMessages = [
        {
          ...message,
          createdAt: message.createdAt || new Date(),
          acknowledgments: message.acknowledgments || [],
          recipients: message.recipients || [],
          departments: allDepartments
        },
        ...state.messages
      ].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

      return {
        messages: newMessages,
        messageHistory: [state.messages, ...state.messageHistory].slice(0, 10)
      };
    });
  },
  
  acknowledgeMessage: async (messageId: string, userId: string) => {
    try {
      // First update Supabase
      const { data, error } = await supabase
        .from('message_acknowledgments')
        .insert({
          message_id: messageId,
          user_id: userId,
          acknowledged_at: new Date().toISOString()
        })
        .select(`
          *,
          message:messages!message_acknowledgments_message_id_fkey (
            *,
            sender:profiles!messages_sender_id_fkey (*),
            acknowledgments:message_acknowledgments (
              *,
              user:profiles(*)
            )
          )
        `)
        .single();

      if (error) {
        logger.error('Failed to insert message acknowledgment:', error);
        throw error;
      }

      // Then update local state
      set((state) => {
        const newMessages = state.messages.map((msg) =>
          msg.id === messageId && !msg.acknowledgments.includes(userId)
            ? {
                ...msg,
                acknowledgments: [...msg.acknowledgments, userId],
                isAcknowledged: true,
              }
            : msg
        );
        return {
          messages: newMessages,
          messageHistory: [state.messages, ...state.messageHistory].slice(0, 10)
        };
      });
    } catch (error) {
      logger.error('Failed to acknowledge message:', error);
      throw error;
    }
  },
  
  archiveMessage: async (messageId: string) => {
    try {
      // First update Supabase
      const { error } = await supabase
        .from('messages')
        .update({ archived: true })
        .eq('id', messageId);

      if (error) throw error;

      // Then update local state
      set((state) => {
        const newMessages = state.messages.filter((msg) => msg.id !== messageId);
        return {
          messages: newMessages,
          messageHistory: [state.messages, ...state.messageHistory].slice(0, 10)
        };
      });
    } catch (error) {
      logger.error('Failed to archive message:', error);
      throw error;
    }
  },

  editMessage: (messageId, updates) => {
    set((state) => {
      const newMessages = state.messages.map((msg) =>
        msg.id === messageId
          ? { 
              ...(updates instanceof Object ? updates : msg),
              updatedAt: new Date()
            }
          : msg
      );
      return {
        messages: newMessages,
        messageHistory: [state.messages, ...state.messageHistory].slice(0, 10)
      };
    });
  },

  undoLastAction: () => {
    set((state) => {
      if (state.messageHistory.length === 0) return state;
      const [lastState, ...remainingHistory] = state.messageHistory;
      return {
        messages: lastState,
        messageHistory: remainingHistory
      };
    });
  },

  setSearchQuery: (query) => {
    set({ searchQuery: query });
  },

  getFilteredMessages: () => {
    const state = get();
    const query = state.searchQuery.toLowerCase();
    
    if (!query) return state.messages;

    return state.messages.filter(message => 
      message.title.toLowerCase().includes(query) ||
      message.content.toLowerCase().includes(query) ||
      message.sender.name.toLowerCase().includes(query) ||
      message.departments.some(dept => dept.toLowerCase().includes(query))
    );
  }
});